import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { RequestParams, RequestAttribute } from '../../../utils/models/http.interface';
import { ConfigApiDynamicTable } from '../../../utils/models/table.interface';

@Injectable({
  providedIn: 'root'
})
export class HttpService {
  private readonly apiUrl: string = environment.apiUrl;

  constructor(private readonly http: HttpClient) { }

  genericGet<T>(endpoint: string, id?: string, params?: HttpParams): Observable<T> {
    const url = id ? `${this.apiUrl}/${endpoint}/${id}` : `${this.apiUrl}/${endpoint}`;
    return this.http.get<T>(url, { params })
      .pipe(map((response: T) => response));
  }

  genericGetDownload<T>(endpoint: string, id?: string): Observable<T> {
    const url = id ? `${this.apiUrl}/${endpoint}/${id}` : `${this.apiUrl}/${endpoint}`;
    return this.http.get(url, {responseType: 'blob'})
      .pipe(map((response: any) => response));
  }

  genericGetSelectInfinite<T>(configApi: ConfigApiDynamicTable, endpoint: string, id?: string, requestParams?: RequestParams,  attribute?: RequestAttribute[]): Observable<T> {
    const url = id ? `${configApi.apiUrl}/${endpoint}/${id}` : `${configApi.apiUrl}/${endpoint}`;
    let params = new HttpParams()
    .set('page', requestParams.page.toString())
    .set('sort', requestParams.sort)
    .set('limit', requestParams.limit.toString());
  if (!!attribute) {
    attribute.forEach(
      (att) => (params = params.append(att.param, att.value))
    );
  }
    return this.http.get<T>(url, {
      headers: {
        Authorization: `Bearer ${configApi.token}`
      },
      params
    })
      .pipe(map((response: T) => response));
  }

  genericGetSelectDashboard<T>(
    configApi: ConfigApiDynamicTable,
    endpoint: string,
    requestParams?: RequestParams,
    attribute?: RequestAttribute[]
  ): Observable<any> {
    let params = new HttpParams()
    .set('page', requestParams.page.toString())
    .set('sort', requestParams.sort)
    .set('limit', requestParams.limit.toString());
    if (!!attribute) {
      attribute.forEach(
        (att) => (params = params.append(att.param, att.value))
      );
    }
    return this.http.get<T>(`${configApi.apiUrl}/${endpoint}`, {
      headers: {
        Authorization: `Bearer ${configApi.token}`
      },
      params
    }).pipe(map((response: T) => response));
  }

  genericGetWithId<T>(endpoint: string, id: string, attribute?: RequestAttribute[]): Observable<T> {
    const url = `${this.apiUrl}/${endpoint}/${id}`;
    let params = new HttpParams();
    if (!!attribute) {
      attribute.forEach((att) => params = params.append(att.param, att.value));
    }
    return this.http.get<T>(url, { params })
      .pipe(map((response: T) => {
        return response;
      }));
  }

  genericGetParams<T>(endpoint: string, attribute: RequestAttribute[]): Observable<T> {
    const url = `${this.apiUrl}/${endpoint}`;
    let params = new HttpParams();
    if (!!attribute) {
      attribute.forEach((att) => params = params.append(att.param, att.value));
    }
    return this.http.get<T>(url, { params })
      .pipe(map((response: T) => {
        return response;
      }));
  }

  genericGetListTable<T>(endpoint: string, requestParams: RequestParams, attribute?: RequestAttribute[], filterAttribute?: RequestAttribute[]): Observable<T> {
    const url = `${this.apiUrl}/${endpoint}`;
    let params = new HttpParams()
      .set('skip', requestParams.skip?.toString())
      .set('sort', requestParams.sort)
      .set('limit', requestParams.limit?.toString());
    if (!!attribute) {
      attribute.forEach((att) => params = params.append(att.param, att.value));
    }
    if (!!filterAttribute) {
      filterAttribute.forEach((att) => params = params.append(att.param, att.value));
    }
    return this.http.get<T>(url, { params })
      .pipe(map((response: T) => {
        return response;
      }));
  }

  genericPost<T>(endpoint: string, data: any, headers: any): Observable<T> {
    const url = `${this.apiUrl}/${endpoint}`;
    
    // Se for FormData, não adiciona headers (o browser adiciona automaticamente com boundary)
    if (data instanceof FormData) {
      return this.http.post<T>(url, data)
        .pipe(map((response: T) => {
          return response;
        }));
    }
    
    const httpOptions = {
      headers: new HttpHeaders(headers)
    };
    return this.http.post<T>(url, data, httpOptions)
      .pipe(map((response: T) => {
        return response;
      }));
  }

  genericPostParams<T>(endpoint: string, requestParams: RequestParams, attribute?: RequestAttribute[], data?: any): Observable<T> {
    let params = new HttpParams()
      .set('page', requestParams.page.toString())
      .set('sort', requestParams.sort)
      .set('order', requestParams.order)
      .set('limit', requestParams.limit.toString());
    if (!!attribute) {
      attribute.forEach((att) => params = params.set(att.param, att.value));
    }
    const url = `${this.apiUrl}/${endpoint}`;
    return this.http.post<T>(url, data, { params })
      .pipe(map((response: T) => response));
  }

  genericPostDownload<T>(endpoint: string, body: any, attribute?: RequestAttribute[]): Observable<T> {
    const url = `${this.apiUrl}/${endpoint}`;
    let params = new HttpParams();

    if (!!attribute) {
      attribute.forEach((att) => params = params.append(att.param, att.value));
    }
    return this.http.post(url, body, {responseType:'arraybuffer'})
      .pipe((response: any) => {
        return response;
      });
  }

  genericPostNotData<T>(endpoint: string, data: any): Observable<T> {
    const url = `${this.apiUrl}/${endpoint}`;
    return this.http.post<T>(url, data)
      .pipe(map((response: T) => response));
  }

  genericPut<T>(endpoint: string, data: any, id?: string): Observable<T> {
    const url = id ? `${this.apiUrl}/${endpoint}/${id}` : `${this.apiUrl}/${endpoint}`

    return this.http.put<T>(url, data)
      .pipe(map((response: T) => response));
  }

  genericDelete<T>(endpoint: string, id: string): Observable<T> {
    const url = `${this.apiUrl}/${endpoint}/${id}`;
    return this.http.delete<T>(url)
      .pipe(map((response: T) => response));
  }

  genericDeleteBody<T>(endpoint: string, id: string, data: any): Observable<T> {
    const options = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
      body:data
    };

    const url = `${this.apiUrl}/${endpoint}/${id}`;
    return this.http.delete<T>(url, options)
      .pipe(map((response: T) => {
        return response;
      }));
  }

  genericDeleteAll<T>(endpoint: string, id: string, attribute?: RequestAttribute[]): Observable<T> {
    const url = `${this.apiUrl}/${endpoint}/${id}`;
    let params = new HttpParams();
    if (!!attribute) {
      attribute.forEach((att) => params = params.append(att.param, att.value));
    }
    return this.http.delete<T>(url, { params })
      .pipe(map((response: T) => {
        return response;
      }));
  }

  genericPutBody<T>(endpoint: string, body: any, attribute?: RequestAttribute[]): Observable<T> {
    const url = `${this.apiUrl}/${endpoint}`;
    let params = new HttpParams()
    if (!!attribute) {
      attribute.forEach((att) => params = params.append(att.param, att.value));
    }
    return this.http.put<T>(url, body,{params})
      .pipe(map((response: T) => {
        return response;
      }));
  }

  genericGetSelectFiltroScroll<T>(
    configApi: ConfigApiDynamicTable,
    endpoint: string,
    requestParams: RequestParams,
    attribute?: RequestAttribute[]
  ): Observable<T> {

    const url = `${configApi.apiUrl}/${endpoint}`;
    let params = new HttpParams()
      .set('page', requestParams.page.toString())
      .set('sort', requestParams.sort)
      .set('limit', requestParams.limit.toString());
    if (!!attribute) {
      attribute.forEach(
        (att) => (params = params.append(att.param, att.value))
      );
    }
    return this.http.get<T>(url, {
      headers: {
        Authorization: `Bearer ${configApi.token}`
      },
      params
    }).pipe(
      map((response: T) => {
        return response;
      })
    );
  }
}
